/**
 * Contract Date Toast Notifications
 * Formats and displays toast notifications for contract date calculations
 */

import { toast } from 'sonner';
import type { I18n } from '@lingui/core';
import { formatDateLong } from '@/lib/format';
import { t, msg } from '@lingui/macro';

/**
 * Shows one toast for calculated contract dates, with the kindergarten start
 * as a second description line when there is one
 *
 * @param i18n - Lingui i18n instance for translations
 * @param parsedDates - Parsed dates from calculation response
 */
export function showContractDatesToasts(
  i18n: I18n,
  parsedDates: {
    startDate: Date;
    endDate: Date | null;
    kindergartenDate?: Date;
  }
): void {
  const { startDate, endDate, kindergartenDate } = parsedDates;

  // Format dates for display in toast
  const formattedStartDate = formatDateLong(startDate, i18n.locale);
  const formattedEndDate = endDate ? formatDateLong(endDate, i18n.locale) : null;

  // The kindergarten information joins the one toast as a second description
  // line instead of a toast of its own.
  const kindergartenLine = kindergartenDate
    ? i18n._(
        msg({
          id: "contracts.autoCalc.kindergartenInfo",
          message: "Contract ends when kindergarten starts on {kindergartenDate}",
        }),
        { kindergartenDate: formatDateLong(kindergartenDate, i18n.locale) }
      )
    : null;
  const withKindergarten = (datesLine: string) =>
    kindergartenLine ? (
      <>
        <span className="block">{datesLine}</span>
        <span className="block">{kindergartenLine}</span>
      </>
    ) : (
      datesLine
    );
  const duration = kindergartenLine ? 6000 : 5000;

  // Show appropriate success message based on whether end date was calculated
  if (formattedEndDate) {
    toast.success(
      i18n._(
        t({
          id: "contracts.autoCalc.success",
          message: "Contract dates calculated successfully",
        })
      ),
      {
        description: withKindergarten(
          i18n._(
            msg({
              id: "contracts.autoCalc.success.description",
              message: "Start: {start}, End: {end}",
            }),
            { start: formattedStartDate, end: formattedEndDate }
          )
        ),
        duration,
      }
    );
  } else {
    // Show start-only toast for open-ended contracts
    toast.info(
      i18n._(
        t({
          id: "contracts.autoCalc.startOnly",
          message: "Contract start date calculated",
        })
      ),
      {
        description: withKindergarten(
          i18n._(
            msg({
              id: "contracts.autoCalc.startOnly.description",
              message: "Start: {start} (open-ended contract)",
            }),
            { start: formattedStartDate }
          )
        ),
        duration,
      }
    );
  }
}
